import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  concat,
  debounceTime,
  distinctUntilChanged,
  filter,
  from,
  groupBy,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  OperatorFunction,
  share,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';

import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { Nullable } from '@ps/utils';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';
import {
  activeSupplierIds,
  mapSurveyLocalization,
  AdvancedFeasibilityFilters,
  CHILD_FEASIBILITY_UNAVAILABLE_MESSAGE,
  feasibilityErrorMessage,
  ComputeFeasibilityParams,
  ComputeFeasibilityParamsForPreDraft,
  FeasibilityEstimate,
  Qualification,
  SupplierAllocation,
  SupplierBlend,
  SupplierManagementService,
  SurveyKind,
  SurveyMetadataForm,
} from '@ps/survey-utils';
import {
  SupplierActions,
  SupplierApiActions,
  SupplierSelectorsFacade,
  SupplierService,
} from '@ps/survey-data-access';

import { FeasibilityService } from '../../services/feasibility/feasibility.service';
import { FlexibilityPricesService } from '../../services/flexibility-prices/flexibility-prices.service';
import { PurePriceCpiService } from '../../services/pure-price-cpi/pure-price-cpi.service';
import { SurveyScoreService } from '../../services/survey-score/survey-score.service';
import { SurveyCreationSelectorsFacade } from '../survey-creation.selectors.facade';

import {
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from '../core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationChildrenActions,
  SurveyCreationChildrenApiActions,
} from '../audience/survey-creation-audience.actions';
import {
  selectAdvancedFeasibilityFilters,
  selectAudienceChildren,
  selectIsPreDraft,
  selectMarginForm,
  selectMetadataForm,
  selectSurveyFormState,
} from '../audience/survey-creation-audience.selectors';
import {
  selectActiveChildKey,
  selectCoreChildren,
  selectSurveyAllowsRecalculation,
  selectRootSurveyId,
  selectSurvey,
  selectSurveyId,
} from '../core/survey-creation-core.selectors';

import {
  SurveyCreationReferenceActions,
  SurveyCreationReferenceApiActions,
} from '../reference/survey-creation-reference.actions';
import {
  SurveyChildRegistryState,
  SurveyCreationChildAudienceState,
} from '../survey-creation-state.model';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import { SurveyAudienceForm } from '../../utils';
import {
  allowsRecalculation,
  computeFeasibilityParams,
  computeFeasibilityParamsForPreDraft,
  debounceEdits,
  debounceEditsPerKey,
  allowsRecalculationForMetadataPatch,
  qualifiesForRecalculation,
  resolveRecalculationKeys,
  shouldMarkAsPending,
  toRecalculationChildTasks,
  toSupplierContext,
} from './utils/survey-creation-recalculation.helper';
import {
  RecalculationData,
  RecalculationTrigger,
} from './utils/survey-creation-recalculation.types';

import {
  SurveyCreationInsightsActions,
  SurveyCreationInsightsApiActions,
  SurveyCreationInsightsPreDraftActions,
  SurveyCreationInsightsPreDraftApiActions,
} from './survey-creation-insights.actions';
import {
  selectInsightsChildren,
  selectPricing,
} from './survey-creation-insights.selectors';

@Injectable()
export class SurveyCreationInsightsEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);

  private readonly _store = inject(Store);

  private readonly _surveyScoreService = inject(SurveyScoreService);

  private readonly _feasibilityService = inject(FeasibilityService);

  private readonly _supplierService = inject(SupplierService);

  private readonly _flexibilityPricesService = inject(FlexibilityPricesService);
  private readonly _toastService = inject(ToastService);

  private readonly _purePriceCpiService = inject(PurePriceCpiService);

  private readonly _authSelectors = inject(AuthSelectorsFacade);

  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);

  private readonly _surveyCreationSelectors = inject(
    SurveyCreationSelectorsFacade,
  );

  private readonly _supplierSelectors = inject(SupplierSelectorsFacade);

  private readonly _supplierEngine = inject(SupplierManagementService);

  public readonly computeScore$ = createEffect(() => {
    const surveyChangeTrigger$ = this._actions$.pipe(
      ofType(
        SurveyCreationAudienceActions.updateMetadata,
        // Score is priced on buyer CPI, so a settle re-asks; not updateMargin,
        // which fires per keystroke and doesn't feed the score at all.
        SurveyCreationAudienceActions.settleBuyerCpi,
        SurveyCreationAudienceActions.toggleManualProcessing,
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
        SurveyCreationInsightsActions.refreshAfterSave,
      ),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectSurveyAllowsRecalculation),
      ]),
      filter(
        ([action, surveyId, surveyAllowsIt]) =>
          !!surveyId && surveyAllowsIt && allowsRecalculation(action),
      ),
    );

    return merge(
      surveyChangeTrigger$.pipe(
        filter(([action]) => shouldMarkAsPending(action)),
        map(() => SurveyCreationInsightsApiActions.computeScorePending()),
      ),
      surveyChangeTrigger$.pipe(
        filter(([action]) =>
          qualifiesForRecalculation(
            action,
            !!this._userSettingsSelectors.buyerSettings()
              .enableManualProcessing,
          ),
        ),
        debounceTime(500),
        map(([, surveyId]) => Option.from(surveyId).unwrap()),
        concatLatestFrom(() => [
          this._store.select(selectMetadataForm),
          this._store.select(selectMarginForm),
        ]),
        filter(([surveyId, metadata]) => !!surveyId && !!metadata?.valid),
        map(([surveyId, metadata, margin]) => ({
          surveyId,
          cpi: Option.from(margin.value).mapOr(0, ({ buyerCpi }) =>
            Option.from(buyerCpi).unwrapOr(0),
          ),
          value: Option.from(metadata.value).map((value) => ({
            loi: Option.from(value.loi).unwrapOr(0),
            incidence: Option.from(value.incidence).unwrapOr(0),
          })),
        })),
        filter(({ value }) => value.isSome()),
        map(({ surveyId, cpi, value }) => ({
          surveyId,
          cpi,
          value: value.unwrap(),
        })),
        switchMap(({ surveyId, cpi, value }) =>
          this._surveyScoreService
            .computeScore(surveyId, {
              cpi,
              ir: value.incidence,
              loi: value.loi,
            })
            .pipe(
              map(({ score, scorePercentage }) =>
                SurveyCreationInsightsApiActions.computeScoreSuccess({
                  score,
                  scorePercentage,
                }),
              ),
              this.psCatchError((error) =>
                of(
                  SurveyCreationInsightsApiActions.computeScoreFailure({
                    error: error.message,
                  }),
                ),
              ),
            ),
        ),
      ),
    );
  });

  // Active load/refresh/filters/toggle, the scoped audience edits, and a
  // sibling list refresh all funnel through one pipeline: gated on a real
  // survey existing, then fanned out to whichever children each targets.
  public readonly recalculateFeasibility$ = createEffect(() => {
    const trigger$ = this._recalculationTrigger$();
    return merge(
      this._recalculationPendingActions$(trigger$),
      this._recalculationResultActions$(trigger$),
    );
  });

  // When the survey is on system-managed PurePrice (defaultPurePriceOverride),
  // every audience change re-asks the pure-price endpoint and writes the result
  // in as the new global override — into insights offerPrice and the audience
  // suppliersMetaData.priceOverride. The flag itself is left on; a manual Apply
  // is the only thing that turns it off (see the drawer mappers).
  public readonly recalculatePurePriceOverride$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
        SurveyCreationInsightsActions.refreshAfterSave,
        SurveyCreationAudienceActions.updateMetadata,
        SurveyCreationAudienceActions.updateQualifications,
        SurveyCreationAudienceActions.updateQuotas,
        SurveyCreationAudienceActions.applyAdvancedFilters,
        SurveyCreationAudienceActions.toggleManualProcessing,
      ),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(
        ([action, surveyId]) =>
          !!surveyId &&
          this._surveyCreationSelectors.suppliersMetaData()
            .defaultPurePriceOverride &&
          qualifiesForRecalculation(
            action,
            !!this._userSettingsSelectors.buyerSettings()
              .enableManualProcessing,
          ),
      ),
      debounceTime(500),
      concatLatestFrom(() => [
        this._store.select(selectSurveyFormState),
        this._store.select(selectSurvey),
      ]),
      map(([[, surveyId], formState, survey]) => ({
        surveyId: Option.from(surveyId).unwrap(),
        survey: Option.from(survey),
        form: SurveyAudienceForm.from(formState),
      })),
      filter(({ form }) => form.valid),
      map(({ surveyId, survey, form }) => {
        const formData = Option.from(form.data).unwrap();
        return {
          user: this._userData(),
          surveyId,
          survey,
          ...formData,
          catalogQualifications: this._catalogQualifications(formData.metadata),
        };
      }),
      switchMap((data) => this._recalculatePurePrice$(data)),
    ),
  );

  public readonly refreshActiveChildAfterCpiUpdate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([{ survey }, activeSurveyId]) => survey.id === activeSurveyId),
      map(() => SurveyCreationInsightsActions.refreshAfterSave()),
    ),
  );

  public readonly preDraftFeasibility$ = createEffect(() => {
    const trigger$ = this._actions$.pipe(
      ofType(
        SurveyCreationInsightsPreDraftActions.calculateFeasibilityForPreDraft,
        SurveyCreationAudienceActions.updateQualifications,
        SurveyCreationAudienceActions.updateQuotas,
      ),
      debounceTime(50),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectIsPreDraft),
        this._store.select(selectAudienceChildren),
      ]),
      filter(([, surveyId, isPreDraft]) => !surveyId && isPreDraft),
      map(([, , , children]) => Object.entries(children)),
      share(),
    );

    return merge(
      trigger$.pipe(
        mergeMap((entries) =>
          entries.map(([key]) =>
            SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftPending(
              { key },
            ),
          ),
        ),
      ),
      trigger$.pipe(
        mergeMap((entries) => {
          // Tracker's wave clones share identical params — dedupe per-trigger
          // so they share one HTTP call instead of N identical ones.
          const sharedRequests = new Map<
            string,
            Observable<FeasibilityEstimate>
          >();

          return entries
            .map(([key, child]) => ({
              key,
              form: SurveyAudienceForm.from({
                metadata: child.metadata,
                qualifications: child.qualifications,
                quotas: child.quotas,
              }),
            }))
            .filter(({ form }) => form.valid)
            .map(({ key, form }) => {
              const params = computeFeasibilityParamsForPreDraft(
                this._userData(),
                Option.from(form.data).unwrap(),
              );

              return {
                key,
                request$: this._sharedFeasibilityRequest(
                  sharedRequests,
                  params,
                ),
              };
            });
        }),
        groupBy(({ key }) => key),
        mergeMap((group$) =>
          group$.pipe(
            switchMap(({ key, request$ }) =>
              request$.pipe(
                map((estimate) =>
                  SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftSuccess(
                    { key, ...estimate },
                  ),
                ),
                this.psCatchError((error) =>
                  of(
                    SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftFailure(
                      { key, error: error.message },
                    ),
                  ),
                ),
              ),
            ),
          ),
        ),
      ),
    );
  });

  // Seeds the supplier allocations slice when an existing survey is opened for
  // edit. Fetches the editable-scope allocations once off `loadActiveSurveyForEditSuccess`
  // and stores them via `loadAllocationsSuccess`, so downstream effects
  // (`redistributeOnCompletesChange$`) and the drawer read a populated slice.
  public readonly seedSuppliersOnEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectSurveyFormState),
        this._store.select(selectSurvey),
        this._store.select(selectSurveyAllowsRecalculation),
      ]),
      filter(
        ([action, surveyId, _, __, surveyAllowsIt]) =>
          !!surveyId && surveyAllowsIt && allowsRecalculation(action),
      ),
      map(([, surveyId, formState, survey]) => ({
        surveyId: Option.from(surveyId).unwrap(),
        survey: Option.from(survey),
        form: SurveyAudienceForm.from(formState),
      })),
      filter(({ form }) => form.valid),
      map(({ surveyId, survey, form }) => {
        const formData = Option.from(form.data).unwrap();
        return {
          user: this._userData(),
          surveyId,
          survey: Option.from(survey),
          ...formData,
          catalogQualifications: this._catalogQualifications(formData.metadata),
        };
      }),
      switchMap((data) =>
        this._supplierService
          .allocations(data.surveyId, {
            scope: 'editable',
            context: toSupplierContext(data),
          })
          .pipe(
            map((allocations) =>
              SupplierApiActions.loadAllocationsSuccess({ allocations }),
            ),
            this.psCatchError(
              (error) =>
                of(
                  SupplierApiActions.loadAllocationsFailure({
                    error:
                      error.message ?? 'Failed to load supplier allocations',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public readonly syncActiveChild$ = createEffect(() =>
    this._store.select(selectSurveyId).pipe(
      distinctUntilChanged(),
      map((activeChildId) =>
        SurveyCreationInsightsActions.syncActiveChild({ activeChildId }),
      ),
    ),
  );

  // Re-spreads the supplier allocation quantities on every metadata update in
  // edit mode, using the current completes (goal). Persists locally through
  // `loadAllocationsSuccess` — no PUT.
  public readonly redistributeOnCompletesChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.updateMetadata),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      debounceTime(500),
      map(([{ metadata }]) =>
        Option.from(metadata.value?.completes).unwrapOr(0),
      ),
      filter((goal) => goal > 0),
      map((goal) => {
        const current = this._supplierSelectors.allocations();
        const next = this._surveyCreationSelectors.suppliersMetaData()
          .supplierDefaultAllocation
          ? this._supplierEngine.redistributeAllocationsByDefault(
              current,
              this._supplierSelectors.defaultAllocations(),
              goal,
            )
          : this._resolveBlend().match({
              some: (blend) =>
                this._supplierEngine.redistributeAllocationsByBlend(
                  current,
                  blend,
                  goal,
                ),
              none: () =>
                this._supplierEngine.redistributeAllocationsEvenly(
                  current,
                  goal,
                ),
            });
        return SupplierApiActions.loadAllocationsSuccess({ allocations: next });
      }),
    ),
  );

  // Loads the saved supplier blends for the survey's locale when an existing
  // survey opens for edit, so `redistributeOnCompletesChange$` can resolve the
  // applied blend even before the CPI / supplier drawer is opened.
  public readonly loadBlendsOnEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess),
      concatLatestFrom(() => [
        this._store.select(selectSurvey),
        this._store.select(selectSurveyAllowsRecalculation),
      ]),
      filter(
        ([action, , surveyAllowsIt]) =>
          surveyAllowsIt && allowsRecalculation(action),
      ),
      map(([, survey]) => Option.from(survey)),
      filter((survey) => survey.isSome()),
      map((survey) => survey.unwrap()),
      map((survey) =>
        SupplierActions.loadBlends({
          locale: mapSurveyLocalization(
            survey.countryCode,
            survey.languageShortCode,
          ),
        }),
      ),
    ),
  );

  // The update PUT's response already carries the new allocations, so this
  // re-runs only flexibility → feasibility, off the action, not a refetch.
  public readonly refreshPricingAfterSupplierUpdate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.updateAllocationsSuccess),
      concatLatestFrom(() => [
        this._store.select(selectActiveChildKey),
        this._store.select(selectSurveyId),
        this._store.select(selectSurveyFormState),
        this._store.select(selectSurvey),
        this._store.select(selectAdvancedFeasibilityFilters),
        this._store.select(selectPricing),
        this._store.select(selectSurveyAllowsRecalculation),
      ]),
      filter(
        ([, activeChildKey, surveyId, , , , , surveyAllowsIt]) =>
          !!activeChildKey && !!surveyId && surveyAllowsIt,
      ),
      map(
        ([
          { allocations },
          activeChildKey,
          surveyId,
          formState,
          survey,
          advancedFilters,
          pricing,
        ]) => ({
          allocations,
          key: Option.from(activeChildKey).unwrap(),
          surveyId: Option.from(surveyId).unwrap(),
          survey: Option.from(survey),
          form: SurveyAudienceForm.from(formState),
          advancedFilters,
          offerPrice: pricing.offerPrice,
        }),
      ),
      filter(({ form }) => form.valid),
      map(
        ({
          allocations,
          key,
          surveyId,
          survey,
          form,
          advancedFilters,
          offerPrice,
        }) => {
          const formData = Option.from(form.data).unwrap();
          return {
            allocations,
            key,
            advancedFilters,
            offerPrice,
            data: {
              user: this._userData(),
              surveyId,
              survey: Option.from(survey),
              ...formData,
              catalogQualifications: this._catalogQualifications(
                formData.metadata,
              ),
            },
          };
        },
      ),
      switchMap(({ data, allocations, key, advancedFilters, offerPrice }) =>
        concat(
          of(
            SurveyCreationInsightsApiActions.calculateFeasibilityPending({
              key,
            }),
          ),
          this._loadFlexibilityPrices$(
            key,
            data,
            advancedFilters,
            offerPrice,
            allocations,
          ),
        ),
      ),
    ),
  );

  // The applied blend, resolved from the loaded blends by the survey-creation
  // blend id; `none` when no blend is set or it isn't loaded — caller falls back
  // to even distribution.
  private _resolveBlend(): Option<SupplierBlend> {
    const blendId = this._surveyCreationSelectors.suppliersMetaDataBlendId();
    if (!blendId) return Option.none;
    return Option.from(
      this._supplierSelectors.blends().find((blend) => blend.id === blendId),
    );
  }

  private _recalculationTrigger$(): Observable<RecalculationTrigger> {
    return this._actions$.pipe(
      ofType(
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
        SurveyCreationInsightsActions.refreshAfterSave,
        SurveyCreationAudienceActions.updateMetadata,
        SurveyCreationAudienceActions.updateQualifications,
        SurveyCreationAudienceActions.updateQuotas,
        SurveyCreationChildrenActions.patchChildMetadata,
        SurveyCreationAudienceActions.applyAdvancedFilters,
        SurveyCreationAudienceActions.toggleManualProcessing,
        SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess,
        SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess,
      ),
      concatLatestFrom(() => [
        this._store.select(selectRootSurveyId),
        this._store.select(selectSurveyAllowsRecalculation),
        this._store.select(selectMetadataForm),
      ]),
      filter(
        ([action, rootSurveyId, surveyAllowsIt, metadata]) =>
          !!rootSurveyId &&
          surveyAllowsIt &&
          allowsRecalculationForMetadataPatch(action, metadata.valid) &&
          allowsRecalculation(action),
      ),
      map(([action]) => action),
      share(),
    );
  }

  // Marks every resolved key pending, ahead of the actual recalculation.
  // Shares `_settledRecalculationKeys` with `_recalculationResultActions$`
  // so both resolve the same keys — otherwise a switch can leave a stale key
  // stuck `loading`.
  private _recalculationPendingActions$(
    trigger$: Observable<RecalculationTrigger>,
  ): Observable<Action> {
    return trigger$.pipe(
      filter((action) => shouldMarkAsPending(action)),
      this._settledRecalculationKeys(),
      map((key) =>
        SurveyCreationInsightsApiActions.calculateFeasibilityPending({ key }),
      ),
    );
  }

  // Resolves each qualifying trigger to its target children (see
  // debounceEdits for why loads skip the debounce) and runs the full
  // pipeline per child, isolated by key so one failure never blocks another.
  private _recalculationResultActions$(
    trigger$: Observable<RecalculationTrigger>,
  ): Observable<Action> {
    return trigger$.pipe(
      filter((action) =>
        qualifiesForRecalculation(
          action,
          !!this._userSettingsSelectors.buyerSettings().enableManualProcessing,
        ),
      ),
      this._settledRecalculationKeys(),
      concatLatestFrom(() => [
        this._store.select(selectCoreChildren),
        this._store.select(selectAudienceChildren),
        this._store.select(selectInsightsChildren),
      ]),
      mergeMap(([key, coreChildren, audienceChildren, insightsChildren]) =>
        from(
          toRecalculationChildTasks(
            [key],
            coreChildren,
            audienceChildren,
            insightsChildren,
          ),
        ),
      ),
      groupBy(({ key }) => String(key)),
      mergeMap((task$) =>
        task$.pipe(
          switchMap(({ key, coreChild, audienceChild, offerPrice }) =>
            this._recalculateChildFeasibility$(
              key,
              coreChild,
              audienceChild,
              offerPrice,
            ),
          ),
        ),
      ),
    );
  }

  private _settledRecalculationKeys(): OperatorFunction<
    RecalculationTrigger,
    SurveyChildKey
  > {
    return (trigger$) => {
      const classified$ = trigger$.pipe(
        concatLatestFrom(() => this._store.select(selectSurvey)),
        map(([action, survey]) => ({
          action,
          isGroupMcOrTracker: !!survey && SurveyKind.isGroupMcOrTracker(survey),
        })),
        share(),
      );

      return merge(
        classified$.pipe(
          filter(({ isGroupMcOrTracker }) => !isGroupMcOrTracker),
          map(({ action }) => action),
          debounceEdits(500),
          this._resolveKeys(),
          mergeMap(({ keys }) => keys),
        ),
        classified$.pipe(
          filter(({ isGroupMcOrTracker }) => isGroupMcOrTracker),
          map(({ action }) => action),
          this._resolveKeys(),
          mergeMap(({ action, keys }) => keys.map((key) => ({ key, action }))),
          debounceEditsPerKey(500),
        ),
      );
    };
  }

  private _resolveKeys(): OperatorFunction<
    RecalculationTrigger,
    { action: RecalculationTrigger; keys: SurveyChildKey[] }
  > {
    return (trigger$) =>
      trigger$.pipe(
        concatLatestFrom(() => [
          this._store.select(selectActiveChildKey),
          this._store.select(selectCoreChildren),
        ]),
        map(([action, activeChildKey, coreChildren]) => ({
          action,
          keys: resolveRecalculationKeys(action, activeChildKey, coreChildren),
        })),
      );
  }

  private _recalculateChildFeasibility$(
    key: SurveyChildKey,
    coreChild: SurveyChildRegistryState,
    audienceChild: SurveyCreationChildAudienceState,
    offerPrice: Nullable<number>,
  ): Observable<Action> {
    const form = SurveyAudienceForm.from({
      metadata: audienceChild.metadata,
      qualifications: audienceChild.qualifications,
      quotas: audienceChild.quotas,
    });

    return Option.from(coreChild.survey)
      .zip(Option.from(form.data).filter(() => form.valid))
      .map(([survey, formData]) => ({ survey, formData }))
      .match({
        some: ({ survey, formData }) =>
          this._childCatalogQualifications$(formData.metadata).pipe(
            switchMap((catalogQualifications) => {
              const data: RecalculationData = {
                user: this._userData(),
                surveyId: survey.id,
                survey: Option.some(survey),
                ...formData,
                catalogQualifications,
              };
              return this._recalculateFlexibilityPricing$(
                key,
                data,
                audienceChild.advancedFeasibilityFilters,
                offerPrice,
              );
            }),
          ),
        none: () =>
          of(
            SurveyCreationInsightsApiActions.calculateFeasibilityFailure({
              key,
              error: CHILD_FEASIBILITY_UNAVAILABLE_MESSAGE,
            }),
          ),
      });
  }

  private _recalculatePurePrice$(data: RecalculationData): Observable<Action> {
    return this._purePriceCpiService
      .compute(data.surveyId, this._purePriceParams(data))
      .pipe(
        mergeMap((cpi) => {
          const allocations = this._supplierSelectors.allocations();
          return [
            SurveyCreationInsightsApiActions.loadPurePriceCpiSuccess({ cpi }),
            SurveyCreationAudienceActions.setSuppliersMetaData({
              metaData: { priceOverride: cpi },
            }),
            // Broadcast the same override CPI onto the supplier allocations,
            // persisted locally via loadAllocationsSuccess (no PUT) — the
            // redistribute pattern.
            ...(allocations.length
              ? [
                  SupplierApiActions.loadAllocationsSuccess({
                    allocations:
                      this._supplierEngine.applyGlobalPriceOverrideToAllocations(
                        allocations,
                        cpi,
                      ),
                  }),
                ]
              : []),
          ];
        }),
        this.psCatchError(
          (error) =>
            of(
              SurveyCreationInsightsApiActions.loadPurePriceCpiFailure({
                error: error.message,
              }),
            ),
          { notify: true },
        ),
      );
  }

  // Pure price is priced off the audience only — the pricing / grouping /
  // supplier fields ComputeFeasibilityParams also carries are neutral here.
  private _purePriceParams(data: RecalculationData): ComputeFeasibilityParams {
    return {
      surveyId: data.surveyId,
      user: data.user,
      metadata: data.metadata,
      qualifications: data.qualifications,
      quotas: data.quotas,
      catalogQualifications: data.catalogQualifications,
      offerPrice: Option.none,
      supplierIds: [],
      surveyGroupIds: [],
      isExclusion: false,
      groupingStatuses: [],
    };
  }

  private _recalculateFlexibilityPricing$(
    key: SurveyChildKey,
    data: RecalculationData,
    advancedFilters: AdvancedFeasibilityFilters,
    offerPrice: Nullable<number>,
  ): Observable<Action> {
    return this._supplierService
      .allocations(data.surveyId, {
        scope: 'editable',
        context: toSupplierContext(data),
      })
      .pipe(
        switchMap((allocations) =>
          // Reflect the recalculation's supplier activate/deactivate flip into
          // the allocations slice (isActive only) before loading prices, without
          // discarding the user's other local edits.
          concat(
            this._storedAllocations$(key, allocations),
            this._loadFlexibilityPrices$(
              key,
              data,
              advancedFilters,
              offerPrice,
              allocations,
            ),
          ),
        ),
        this.psCatchError(
          (error) =>
            of(
              SupplierApiActions.loadAllocationsFailure({
                error: error.message ?? 'Failed to load supplier allocations',
              }),
            ),
          { notify: true },
        ),
      );
  }

  private _storedAllocations$(
    key: SurveyChildKey,
    allocations: SupplierAllocation[],
  ): Observable<Action> {
    return this._store.select(selectSurvey).pipe(
      concatLatestFrom(() => this._store.select(selectActiveChildKey)),
      take(1),
      filter(
        ([survey, activeChildKey]) =>
          !survey ||
          !SurveyKind.isGroupMcOrTracker(survey) ||
          String(key) === String(activeChildKey),
      ),
      map(() => SupplierApiActions.syncAllocationsActiveState({ allocations })),
    );
  }

  private _loadFlexibilityPrices$(
    key: SurveyChildKey,
    data: RecalculationData,
    advancedFilters: AdvancedFeasibilityFilters,
    offerPrice: Nullable<number>,
    allocations: readonly SupplierAllocation[],
  ): Observable<Action> {
    return this._flexibilityPricesService.get(data.surveyId).pipe(
      switchMap((prices) =>
        concat(
          of(
            SurveyCreationInsightsApiActions.calculateFlexibilityPricesSuccess({
              key,
              prices,
            }),
          ),
          this._computeFeasibility$(
            key,
            data,
            advancedFilters,
            offerPrice,
            allocations,
          ),
        ),
      ),
      this.psCatchError(
        (error) =>
          of(
            SurveyCreationInsightsApiActions.calculateFlexibilityPricesFailure({
              key,
              error: error.message ?? 'Failed to load flexibility prices',
            }),
          ),
        { notify: true },
      ),
    );
  }

  // Apply is the commit boundary for Advanced Filters — the request is built
  // from what's already committed, not a second exclusion-free fetch.
  private _computeFeasibility$(
    key: SurveyChildKey,
    data: RecalculationData,
    advancedFilters: AdvancedFeasibilityFilters,
    offerPrice: Nullable<number>,
    allocations: readonly SupplierAllocation[],
  ): Observable<Action> {
    const params = computeFeasibilityParams(
      data,
      advancedFilters,
      activeSupplierIds(allocations),
      this._feasibilityOfferPrice(offerPrice),
    );

    return this._feasibilityService.compute(params).pipe(
      map((estimate) =>
        SurveyCreationInsightsApiActions.calculateFeasibilitySuccess({
          key,
          ...estimate,
        }),
      ),
      this._catchFeasibilityError(data, (error) =>
        SurveyCreationInsightsApiActions.calculateFeasibilityFailure({
          key,
          error,
        }),
      ),
    );
  }

  private _catchFeasibilityError(
    data: RecalculationData,
    toFailure: (error: string) => Action,
  ): OperatorFunction<Action, Action> {
    return this.psCatchError(
      (error) => {
        this._toastService.error(
          feasibilityErrorMessage(
            error.metadata['http'],
            data.quotas.map(({ quotaTitle }) => quotaTitle),
          ),
        );

        return of(toFailure(error.message));
      },
      { metadata: { notify: false } },
    );
  }

  /**
   * Feasibility prices against the buyer's override only when the buyer is
   * entitled to it; otherwise the override is discarded entirely rather than
   * quietly influencing the estimate.
   */
  private _feasibilityOfferPrice(offerPrice: Nullable<number>): Option<number> {
    return this._userSettingsSelectors.buyerSettings()
      .enablePriceOverrideFeasibilityMultiplier
      ? Option.from(offerPrice)
      : Option.none;
  }

  /** Shares one in-flight `computeForPreDraft` call across byte-identical params. */
  private _sharedFeasibilityRequest(
    cache: Map<string, Observable<FeasibilityEstimate>>,
    params: ComputeFeasibilityParamsForPreDraft,
  ): Observable<FeasibilityEstimate> {
    const hash = JSON.stringify(params);
    const cached = cache.get(hash);

    if (cached) {
      return cached;
    }

    const request$ = this._feasibilityService
      .computeForPreDraft(params)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    cache.set(hash, request$);
    return request$;
  }

  private _userData(): { id: number; email: string; companyId: number } {
    const user = this._authSelectors.user().unwrap();
    return {
      id: user.id,
      email: user.email,
      companyId: this._authSelectors.activeCompanyId(),
    };
  }

  // Unfiltered catalog (232 included) — the survey's own qualifications never
  // carry a 232 entry, see RecalculationData.catalogQualifications.
  private _catalogQualifications(
    metadata: SurveyMetadataForm,
  ): Qualification[] {
    return this._surveyCreationSelectors.selectQualifications(
      {
        countryCode: metadata.selectedCountries[0] ?? '',
        languageShortCode: metadata.selectedLanguages[0] ?? '',
      },
      true,
    );
  }

  private _childCatalogQualifications$(
    metadata: SurveyMetadataForm,
  ): Observable<Qualification[]> {
    const cached = this._catalogQualifications(metadata);
    if (cached.length) {
      return of(cached);
    }

    const countryCode = metadata.selectedCountries[0] ?? '';
    const languageShortCode = metadata.selectedLanguages[0] ?? '';

    this._store.dispatch(
      SurveyCreationReferenceActions.loadQualifications({
        countryCode,
        languageShortCode,
      }),
    );

    return this._actions$.pipe(
      ofType(
        SurveyCreationReferenceApiActions.loadQualificationsSuccess,
        SurveyCreationReferenceApiActions.loadQualificationsFailure,
      ),
      filter(
        (action) =>
          action.countryCode === countryCode &&
          action.languageShortCode === languageShortCode,
      ),
      take(1),
      map(() => this._catalogQualifications(metadata)),
    );
  }
}
